import { useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";

// A page-local Back control must ALWAYS have somewhere to go.
//
// The portal has four of them, and they used to disagree about what "back"
// means. Two (the events admin screen, `/events/admin/:eventRecId`, and the
// tournament admin screen, `/tournaments/admin/:id`) called a bare
// `navigate(-1)`: in a fresh tab or an installed PWA there is no history
// entry behind the app, so the button did nothing at all — a dead control on
// a page that draws no portal nav. The other two (`EventStats.jsx`,
// `InviteEvent.jsx`) gated `navigate(-1)` on `window.history.length > 1`,
// which counts the WHOLE TAB's history, entries that belong to other sites
// included: open the portal from a Gmail link and the first press of Back
// walked out of the portal and back to Gmail.
//
// THE ORACLE IS REACT ROUTER'S OWN HISTORY, NOT THE BROWSER'S. React Router
// marks the location the app was LOADED at with the key "default"
// (@remix-run/router createLocation: `globalHistory.state?.key || "default"`)
// and gives every entry it pushes or replaces a generated key. So
//
//   location.key !== "default"
//
// means this app has at least one entry of its own behind the current one and
// `navigate(-1)` returns the user to a page INSIDE the portal. When it is
// "default" the app has not navigated anywhere, and `navigate(-1)` would
// either do nothing (fresh tab / installed app) or leave the portal entirely
// (arrived from another site) — that is the case the fallback exists for, and
// it is the destination already printed on the button.
//
// Why not `window.history.length > 1`: it cannot tell an in-app entry from a
// pre-app one, so it is wrong in both directions — false on the reported
// fresh-tab / installed-app path only when the tab is genuinely empty, and
// true whenever the tab carries any earlier page at all.
//
// The change is strictly narrowing: `location.key !== "default"` implies the
// app pushed an entry, which implies `history.length > 1`, so every press
// that used to go back in history still does. Only the presses that used to
// do nothing, or leave the portal, now take the destination.
//
// THE FALLBACK ARRIVAL IS MARKED. A fallback push leaves the page it fell back
// FROM one entry behind, and that page loaded at "default" too, so the next
// page's `navigate(-1)` would return to it: E-Stats loaded fresh falls back to
// its event page, and the event's Back went straight back to E-Stats, forever
// (Levi, msg 1a188d57b507f4fd, 10/29/26). The marker in location.state says
// "this entry was reached by a fallback, so there is no in-app page to return
// to", and the Back takes its own fallback.
export const useBackNavigation = (fallbackPath) => {
  const navigate = useNavigate();
  const { key, state } = useLocation();
  return useCallback(() => {
    if (key !== "default" && !state?.backFallback) navigate(-1);
    else navigate(fallbackPath, { state: { backFallback: true } });
  }, [navigate, key, state, fallbackPath]);
};

export default useBackNavigation;
